import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ArrowRight, ChevronLeft, ChevronRight, Search, User } from 'lucide-react'
import {
  Company,
  PersonSummary,
  PersonSearchResult,
  SearchOwner,
  SearchResult,
  searchCompanies,
  searchPersons,
} from '../services/apiClient'
import { Badge, Container, CountryFlagIcon, Switch, Tabs } from '../components/ui'
import type { TabDef } from '../components/ui'
import WatchlistIndicator from '../components/watchlist/WatchlistIndicator'
import {
  companyCountryFlagCode,
  detectCountry,
  formatAddressCountryLabel,
  formatCompanyAddress,
  formatCompanyForm,
  formatDate,
  formatPercentRange,
  formatStatusLabel,
  isDissolvedStatus,
  isDistressedStatus,
  stripTrailingCountryFromAddress,
} from '../utils/format'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRecentSearches } from '../hooks/useRecentSearches'

const CVR_NUMBER_PATTERN = /^\d{8}$/
const PAGE_SIZE = 20
type TabId = 'companies' | 'persons'

function parsePageParam(raw: string | null): number {
  const n = parseInt(raw || '1', 10)
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.floor(n)
}

/**
 * Mixed search results — companies and persons shown in a tabbed view.
 * Both searches run in parallel on every query change; switching tabs is
 * instant and costs no extra network request.
 *
 * URL params:
 *   ?q=      — the search query (shared)
 *   ?type=   — active tab: "companies" | "persons" (omitted when "companies")
 *   ?page=   — company page (omitted when 1)
 *   ?ppage=  — person page  (omitted when 1)
 *   ?includeInactive=1 — include dissolved companies (companies tab only)
 *
 * Typing 8 digits still jumps straight to the company profile.
 */
const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const query       = searchParams.get('q') || ''
  const activeTab   = (searchParams.get('type') as TabId | null) ?? 'companies'
  const companyPage = useMemo(() => parsePageParam(searchParams.get('page')),  [searchParams])
  const personPage  = useMemo(() => parsePageParam(searchParams.get('ppage')), [searchParams])
  const includeInactive = searchParams.get('includeInactive') === '1'

  const [draft, setDraft] = useState(query)
  const [companyResult, setCompanyResult] = useState<SearchResult>({
    companies: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE,
  })
  const [personResult, setPersonResult] = useState<PersonSearchResult>({
    persons: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE,
  })
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingPersons,   setLoadingPersons]   = useState(false)
  const [errorCompanies,   setErrorCompanies]   = useState<string | null>(null)
  const [errorPersons,     setErrorPersons]     = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const pageTitle = useMemo(
    () => (query.trim() ? `Søgning: ${query.trim()}` : 'Søgning'),
    [query]
  )
  usePageTitle(pageTitle)

  // »Senest søgt« on the home page
  const { remember } = useRecentSearches()
  useEffect(() => {
    if (query.trim()) remember(query)
  }, [query, remember])

  // Company search — re-runs when query or page changes
  useEffect(() => {
    setDraft(query)
    if (!query) {
      setCompanyResult({ companies: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE })
      setErrorCompanies(null)
      return
    }
    let cancelled = false
    setLoadingCompanies(true)
    setErrorCompanies(null)
    searchCompanies(query, companyPage, PAGE_SIZE, includeInactive)
      .then((data) => { if (!cancelled) setCompanyResult(data) })
      .catch((err) => {
        if (!cancelled) {
          setErrorCompanies(err?.message || 'Søgning fejlede.')
          setCompanyResult({ companies: [], totalCount: 0, page: companyPage, pageSize: PAGE_SIZE })
        }
      })
      .finally(() => { if (!cancelled) setLoadingCompanies(false) })
    return () => { cancelled = true }
  }, [query, companyPage, includeInactive])

  // Person search — also runs in the background so the count is ready when you switch tab
  useEffect(() => {
    if (!query) {
      setPersonResult({ persons: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE })
      setErrorPersons(null)
      return
    }
    let cancelled = false
    setLoadingPersons(true)
    setErrorPersons(null)
    searchPersons(query, personPage, PAGE_SIZE)
      .then((data) => { if (!cancelled) setPersonResult(data) })
      .catch((err) => {
        if (!cancelled) {
          setErrorPersons(err?.message || 'Søgning fejlede.')
          setPersonResult({ persons: [], totalCount: 0, page: personPage, pageSize: PAGE_SIZE })
        }
      })
      .finally(() => { if (!cancelled) setLoadingPersons(false) })
    return () => { cancelled = true }
  }, [query, personPage])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = draft.trim()
    if (!q) return
    if (CVR_NUMBER_PATTERN.test(q)) { navigate(`/company/${q}`); return }
    // New query: reset to tab "companies", page 1
    setSearchParams((prev) => {
      const p = new URLSearchParams()
      p.set('q', q)
      if (prev.get('includeInactive') === '1') p.set('includeInactive', '1')
      return p
    })
  }

  function setIncludeInactive(next: boolean) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next) p.set('includeInactive', '1')
      else p.delete('includeInactive')
      p.delete('page')
      return p
    })
  }

  function setTab(id: TabId) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (id === 'companies') p.delete('type'); else p.set('type', id)
      // Reset pagination for the tab being opened
      if (id === 'companies') p.delete('page'); else p.delete('ppage')
      return p
    })
  }

  function goToCompanyPage(next: number) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next <= 1) p.delete('page'); else p.set('page', String(next))
      return p
    })
  }

  function goToPersonPage(next: number) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next <= 1) p.delete('ppage'); else p.set('ppage', String(next))
      return p
    })
  }

  const tabs: TabDef[] = [
    {
      id: 'companies',
      label: 'Virksomheder',
      count: loadingCompanies ? null : companyResult.totalCount,
    },
    {
      id: 'persons',
      label: 'Personer',
      count: loadingPersons ? null : personResult.totalCount,
    },
  ]

  const companyMaxPage = Math.max(1, Math.ceil(companyResult.totalCount / PAGE_SIZE))
  const personMaxPage  = Math.max(1, Math.ceil(personResult.totalCount  / PAGE_SIZE))

  return (
    <Container className="pt-7 pb-16">
      <div className="max-w-[960px]">
      <div className="mb-5">
        <h1 className="text-[26px] sm:text-[34px] leading-[1.1] text-ink">
          {query ? `Resultater for »${query}«` : 'Find virksomheder og personer'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <label htmlFor="results-search" className="sr-only">
          CVR-nummer, virksomhedsnavn eller personnavn
        </label>
        <div className="flex gap-2">
          <input
            id="results-search"
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="CVR-nummer, virksomhedsnavn eller personnavn"
            className="min-w-0 flex-1 h-12 rounded-lg border border-rule-strong bg-paper px-4 text-base text-ink placeholder:text-ink-400 hover:border-ink-300 transition-colors"
          />
          <button
            type="submit"
            disabled={!draft.trim() || (loadingCompanies && loadingPersons)}
            className="h-12 shrink-0 rounded-lg px-4 sm:px-5 bg-ink text-paper text-sm font-semibold hover:bg-ink-700 disabled:bg-ink-300 inline-flex items-center gap-2 transition-colors"
          >
            <Search className="h-[18px] w-[18px]" aria-hidden />
            <span className="hidden sm:inline">Slå op</span>
          </button>
        </div>
      </form>

      {!query ? (
        <p className="text-sm text-ink-500 mt-4">
          Indtast et CVR-nummer (8 cifre) for direkte opslag, eller skriv et navn
          for at søge i virksomheder og personer.
        </p>
      ) : (
        <>
          <div className="mt-6 border-b border-rule">
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
              <Tabs
                tabs={tabs}
                active={activeTab}
                onChange={(id) => setTab(id as TabId)}
                ariaLabel="Søgeresultater"
                className="mt-0 w-full sm:w-auto sm:flex-1 min-w-0 border-b-0"
              />
              <InactiveCompaniesToggle
                checked={includeInactive}
                onChange={setIncludeInactive}
              />
            </div>
          </div>

          <div className="mt-5">
            {activeTab === 'companies' && (
              <TabPanel
                loading={loadingCompanies}
                error={errorCompanies}
                empty={companyResult.companies.length === 0}
                emptyMessage={`Ingen virksomheder matchede "${query}".`}
                page={companyPage}
                maxPage={companyMaxPage}
                totalCount={companyResult.totalCount}
                pageSize={PAGE_SIZE}
                onPrev={() => goToCompanyPage(companyPage - 1)}
                onNext={() => goToCompanyPage(companyPage + 1)}
              >
                <ol className="list-none overflow-hidden rounded-[10px] border border-rule bg-paper divide-y divide-rule-soft">
                  {companyResult.companies.map((c) => (
                    <CompanyRow key={c.cvrNummer} company={c} />
                  ))}
                </ol>
              </TabPanel>
            )}

            {activeTab === 'persons' && (
              <TabPanel
                loading={loadingPersons}
                error={errorPersons}
                empty={personResult.persons.length === 0}
                emptyMessage={`Ingen personer matchede "${query}".`}
                page={personPage}
                maxPage={personMaxPage}
                totalCount={personResult.totalCount}
                pageSize={PAGE_SIZE}
                onPrev={() => goToPersonPage(personPage - 1)}
                onNext={() => goToPersonPage(personPage + 1)}
              >
                <ol className="list-none overflow-hidden rounded-[10px] border border-rule bg-paper divide-y divide-rule-soft">
                  {personResult.persons.map((p) => (
                    <PersonRow key={String(p.enhedsNummer)} person={p} />
                  ))}
                </ol>
              </TabPanel>
            )}
          </div>
        </>
      )}
      </div>
    </Container>
  )
}

// ── Inactive companies toggle (search tab bar) ────────────────────────────

const InactiveCompaniesToggle: React.FC<{
  checked: boolean
  onChange: (next: boolean) => void
}> = ({ checked, onChange }) => {
  const id = React.useId()
  return (
    <div className="flex shrink-0 items-center gap-2.5 pb-3">
      <label htmlFor={id} className="text-sm text-ink-600 cursor-pointer select-none whitespace-nowrap">
        Inkluder inaktive
      </label>
      <Switch
        id={id}
        checked={checked}
        onChange={onChange}
        ariaLabel="Inkluder inaktive virksomheder"
      />
    </div>
  )
}

// ── Tab panel shell ───────────────────────────────────────────────────────

interface TabPanelProps {
  loading: boolean
  error: string | null
  empty: boolean
  emptyMessage: string
  page: number
  maxPage: number
  totalCount: number
  pageSize: number
  onPrev: () => void
  onNext: () => void
  children: React.ReactNode
}

const TabPanel: React.FC<TabPanelProps> = ({
  loading, error, empty, emptyMessage,
  page, maxPage, totalCount, pageSize,
  onPrev, onNext, children,
}) => {
  const rangeFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeTo   = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount)
  const hasPrev   = page > 1
  const hasNext   = totalCount > 0 && page < maxPage

  if (loading) {
    return (
      <p className="text-[13px] font-semibold text-ink-500">
        Søger…
      </p>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2">
        {error}
      </p>
    )
  }

  if (empty) {
    return <p className="text-sm text-ink-500">{emptyMessage}</p>
  }

  return (
    <>
      {children}
      {maxPage > 1 && (
        <nav
          className="mt-5 flex flex-wrap items-center justify-between gap-4"
          aria-label="Sideinddeling"
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            className="inline-flex h-[38px] items-center gap-1.5 rounded-[7px] border border-rule-strong bg-paper px-3.5 text-sm font-semibold text-ink hover:border-ink-300 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Forrige
          </button>
          <span className="text-sm text-ink-500 tabular-nums">
            Viser {rangeFrom}–{rangeTo} af {totalCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="inline-flex h-[38px] items-center gap-1.5 rounded-[7px] border border-rule-strong bg-paper px-3.5 text-sm font-semibold text-ink hover:border-ink-300 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Næste
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </nav>
      )}
    </>
  )
}

// ── Company row ───────────────────────────────────────────────────────────

const CompanyRow: React.FC<{ company: Company }> = ({ company }) => {
  const fullAddress = formatCompanyAddress(company)
  const country = detectCountry(company.adresse)
  const flagCode = companyCountryFlagCode(company)
  const start = formatDate(company.startdato)
  const showStatus = company.status && (isDissolvedStatus(company.status) || isDistressedStatus(company.status))
  const statusLabel = showStatus ? formatStatusLabel(company.status) : null
  const meta = [
    `CVR ${company.cvrNummer}`,
    formatCompanyForm(company.virksomhedsform),
    start && `Stiftet ${start}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li className="group">
      <Link
        to={`/company/${company.cvrNummer}`}
        className="block px-5 py-4 hover:bg-[#FAFBFC] focus-visible:outline-offset-[-2px] transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-[17px] font-bold tracking-[-0.01em] text-ink inline-flex items-center gap-x-2 gap-y-1 flex-wrap">
              {company.navn || <em className="text-ink-400">Uden navn</em>}
              {flagCode && (
                <CountryFlagIcon code={flagCode} focusable={false} />
              )}
              {statusLabel && (
                <Badge tone={isDissolvedStatus(company.status) ? 'signal' : 'neutral'}>
                  {statusLabel}
                </Badge>
              )}
              <WatchlistIndicator
                entityType="company"
                entityId={String(company.cvrNummer)}
              />
            </h3>
            <p className="mt-1 tabular-nums text-[13.5px] text-ink-500">{meta}</p>
            {company.adresse && (
              <p className="mt-0.5 text-sm text-ink-700">
                {stripTrailingCountryFromAddress(
                  formatAddressCountryLabel(fullAddress),
                  country.isForeign ? country.countryCode : undefined
                )}
              </p>
            )}
            <OwnerLines company={company} />
          </div>
          <ArrowRight
            aria-hidden
            className="h-4 w-4 mt-1.5 shrink-0 text-ink-300 group-hover:text-ink transition-colors"
          />
        </div>
      </Link>
    </li>
  )
}

// ── Owners at a glance (search rows) ──────────────────────────────────────

function ownerStake(o: SearchOwner): string | null {
  if (o.underFive) return 'under 5 %'
  const pct = formatPercentRange(o.percentMin, o.percentMax)
  return pct ? pct.replace(' %–', '–') : null
}

const OwnerName: React.FC<{ owner: SearchOwner }> = ({ owner }) => (
  <span className="inline-flex items-center gap-1">
    <span className="font-semibold text-ink">{owner.name}</span>
    {owner.countryCode && <CountryFlagIcon code={owner.countryCode} focusable={false} />}
    {ownerStake(owner) && <span className="text-[13px] text-ink-500 tabular-nums">{ownerStake(owner)}</span>}
  </span>
)

/**
 * »Ejet af« and »Reel ejer« straight in the result, plus flag chips, so a
 * journalist rarely has to open the profile to see who is behind it.
 */
const OwnerLines: React.FC<{ company: Company }> = ({ company }) => {
  const owners = company.owners ?? []
  const beneficial = company.beneficialOwners ?? []
  const flags = company.flags
  const changed = flags?.ownershipChangedAt ? formatDate(flags.ownershipChangedAt) : null
  const hasChips = !!(flags?.foreignOwner || flags?.noBeneficialOwner || changed)
  if (owners.length === 0 && beneficial.length === 0 && !hasChips) return null

  return (
    <>
      {(owners.length > 0 || beneficial.length > 0) && (
        <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-1 text-sm leading-snug">
          {owners.length > 0 && (
            <>
              <dt className="text-ink-500">Ejet af</dt>
              <dd className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                {owners.map((o, i) => (
                  <React.Fragment key={`${o.name}-${i}`}>
                    {i > 0 && <span className="text-ink-300" aria-hidden>·</span>}
                    <OwnerName owner={o} />
                  </React.Fragment>
                ))}
                {(company.ownersMore ?? 0) > 0 && (
                  <span className="text-[13px] text-ink-500">+{company.ownersMore} flere</span>
                )}
              </dd>
            </>
          )}
          {beneficial.length > 0 && (
            <>
              <dt className="text-ink-500">Reel ejer</dt>
              <dd className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                {beneficial.map((o, i) => (
                  <React.Fragment key={`${o.name}-${i}`}>
                    {i > 0 && <span className="text-ink-300" aria-hidden>·</span>}
                    <span className="inline-flex items-center gap-1">
                      <span className="font-semibold text-ink">{o.name}</span>
                      {o.countryCode && <CountryFlagIcon code={o.countryCode} focusable={false} />}
                    </span>
                  </React.Fragment>
                ))}
              </dd>
            </>
          )}
        </dl>
      )}
      {hasChips && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {flags?.foreignOwner && <Badge tone="signal">Udenlandsk ejer</Badge>}
          {flags?.noBeneficialOwner && <Badge tone="signal">Ingen reel ejer registreret</Badge>}
          {changed && <Badge tone="neutral">Ejerskab ændret {changed}</Badge>}
        </div>
      )}
    </>
  )
}

// ── Person row ────────────────────────────────────────────────────────────

const PersonRow: React.FC<{ person: PersonSummary }> = ({ person }) => {
  const country = detectCountry(person.adresse)
  const flagCode = country.isForeign ? country.countryCode : null

  return (
    <li className="group">
      <Link
        to={`/person/${person.enhedsNummer}`}
        className="block px-5 py-4 hover:bg-[#FAFBFC] focus-visible:outline-offset-[-2px] transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <User
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-ink-400 mt-0.5"
              />
              <h3 className="text-[17px] font-bold tracking-[-0.01em] text-ink inline-flex items-center gap-x-2 gap-y-1 flex-wrap">
                {person.navn || <em className="text-ink-400">Uden navn</em>}
                {flagCode && (
                  <CountryFlagIcon code={flagCode} focusable={false} />
                )}
                <WatchlistIndicator
                  entityType="person"
                  entityId={String(person.enhedsNummer)}
                />
              </h3>
            </div>
            <p className="mt-1 text-[13.5px] text-ink-500 pl-[22px]">
              {person.isAddressConfidential
                ? 'Beskyttet adresse'
                : person.adresse
                ? stripTrailingCountryFromAddress(
                    formatAddressCountryLabel(person.adresse),
                    country.isForeign ? country.countryCode : undefined
                  )
                : null}
            </p>
          </div>
          <ArrowRight
            aria-hidden
            className="h-4 w-4 mt-1.5 shrink-0 text-ink-300 group-hover:text-ink transition-colors"
          />
        </div>
      </Link>
    </li>
  )
}

export default SearchPage
